import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Check, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface PendingLink {
  id: string;
  control_id: string;
  source_type: string;
  source_id: string;
  link_confidence: number | null;
  link_type: string;
  notes: string | null;
  created_at: string;
  effective_from: string;
  effective_to: string | null;
  control: { clause_code: string; title: string; title_id: string | null; framework_id: string } | null;
}

const SOURCE_LABEL: Record<string, string> = {
  document: "Dokumen",
  inspection: "Inspeksi",
  inspection_finding: "Temuan",
  peer_report: "Laporan Bahaya",
  workplace_incident: "Insiden",
  learning_completion: "Pelatihan",
};

export default function AutoLinkReviewQueue({ companyId }: { companyId: string }) {
  const [items, setItems] = useState<PendingLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string>("");

  useEffect(() => {
    if (companyId) void load();
  }, [companyId]);

  async function load() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("compliance_evidence_links")
        .select("id, control_id, source_type, source_id, link_confidence, link_type, notes, created_at, effective_from, effective_to, control:compliance_controls(clause_code, title, title_id, framework_id)")
        .eq("company_id", companyId)
        .eq("review_status", "pending")
        .order("link_confidence", { ascending: false })
        .limit(200);
      if (error) throw error;
      setItems((data || []) as any);
    } catch (e: any) {
      toast.error("Gagal: " + e.message);
    } finally {
      setLoading(false);
    }
  }

  async function review(id: string, status: "accepted" | "rejected") {
    setBusy(id);
    try {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("compliance_evidence_links")
        .update({ review_status: status, reviewed_by: u.user?.id, reviewed_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success(status === "accepted" ? "Diterima" : "Ditolak");
    } catch (e: any) {
      toast.error("Gagal: " + e.message);
    } finally {
      setBusy("");
    }
  }

  async function bulkAccept() {
    if (!confirm(`Terima ${items.length} link bukti?`)) return;
    try {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("compliance_evidence_links")
        .update({ review_status: "accepted", reviewed_by: u.user?.id, reviewed_at: new Date().toISOString() })
        .in("id", items.map((i) => i.id));
      if (error) throw error;
      toast.success("Semua diterima");
      void load();
    } catch (e: any) {
      toast.error("Gagal: " + e.message);
    }
  }

  if (loading) {
    return <div className="p-6 text-muted-foreground"><Loader2 className="w-4 h-4 mr-2 inline animate-spin" />Loading…</div>;
  }

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          Tidak ada usulan link bukti yang menunggu review. Jalankan <strong>Auto-Link</strong> di tab Controls.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{items.length} usulan link bukti menunggu konfirmasi.</p>
        <Button size="sm" variant="outline" onClick={bulkAccept}>Terima semua</Button>
      </div>
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-32">Klausul</TableHead>
              <TableHead>Bukti</TableHead>
              <TableHead className="w-28">Sumber</TableHead>
              <TableHead className="w-24 text-center">Confidence</TableHead>
              <TableHead className="w-32 text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id}>
                <TableCell>
                  <div className="font-mono text-xs">{it.control?.clause_code}</div>
                  <div className="text-xs text-muted-foreground truncate max-w-[160px]">{it.control?.title_id || it.control?.title}</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm line-clamp-2">{it.notes}</div>
                </TableCell>
                <TableCell><Badge variant="outline">{SOURCE_LABEL[it.source_type] || it.source_type}</Badge></TableCell>
                <TableCell className="text-center">
                  <Badge variant="outline" className={
                    (it.link_confidence ?? 0) >= 0.75 ? "bg-success/10 text-success border-success/20" :
                    (it.link_confidence ?? 0) >= 0.5 ? "bg-warning/10 text-warning border-warning/20" :
                    "bg-muted text-muted-foreground"
                  }>{Math.round((it.link_confidence ?? 0) * 100)}%</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => review(it.id, "rejected")} disabled={busy === it.id}>
                      <X className="w-4 h-4" />
                    </Button>
                    <Button size="sm" onClick={() => review(it.id, "accepted")} disabled={busy === it.id}>
                      <Check className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
